/**
 * @file PriceChangeImportModal.js
 * @version 1.3.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-29
 * @modified 2026-10-07
 * @description Read a price list — a screenshot, PDF, spreadsheet, Word
 *              document or pasted text — and queue its prices on the Price
 *              Changes screen after a row-by-row review.
 *
 *              Four ways in: drop a file, pick one, paste an image anywhere in
 *              the window, or paste text (from an email or a workbook) into the
 *              box. Nothing is stored; the file is read and dropped.
 *
 *              Nothing is queued automatically. The server reads each row's code
 *              and name as printed and matches them to items: an exact code or
 *              name match is pre-ticked, a similar-name SUGGESTION is not, and an
 *              unmatched row waits for someone to pick the product. Each price
 *              column is mapped to Cost or Sales price here, by the reviewer,
 *              because a heading like "Price" means cost on a supplier's list
 *              and sale price on ours. Big movements and rows that would replace
 *              a change already queued are flagged.
 *
 *              UNITS. A list prices by the tonne or by the pack; we sell each
 *              item by its own unit (a tonne, or a pack of its unit weight).
 *              Every row carries three editable assumptions — what the printed
 *              price is per, the pack size, and what we sell it per — and the
 *              cost and sale price are converted from the printed figure by
 *              them. Defaults: a price is per tonne unless the list says per
 *              pack; the pack size is what the list shows, else the item's;
 *              we sell per the item's unit (a tonne where it has none). A price
 *              column starts as Cost unless the list says otherwise.
 *
 *              Rows can be ticked and changed together — bulk bar, or right-
 *              click — as well as one at a time.
 *
 *              DATE (2026-10-07): one effective date and one auto-post setting
 *              for the whole import, set in the side panel (today, off by
 *              default). An already-queued change the import updates takes them
 *              too. Shown only when the Price Changes screen has dates
 *              (`scheduling`).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Input from './Input';
import ImportDropZone from './ImportDropZone';
import Dropdown from './Dropdown';
import SearchableDropdown from './SearchableDropdown';
import TickCheckbox from './TickCheckbox';
import TableContextMenu from './TableContextMenu';
import SegToggle from './SegToggle';
import useTableContextMenu from '../hooks/useTableContextMenu';
import { useSlideOutButton } from './SlideOutButton';
import { formatPrice } from '../utils/currencyFormatter';
import { formatDecimalInput } from '../utils/numberFormatters';
import DateInput from './DateInput';
import { formatEffectiveDate, londonToday } from '../utils/priceChangeDates';

const CONFIDENCE_COPY = {
  high: { icon: 'check_circle', text: 'Read cleanly' },
  medium: { icon: 'info', text: 'Check the figures before queuing' },
  low: { icon: 'warning', text: 'Low confidence — check every figure carefully' }
};

const MATCH_COPY = {
  code: { text: 'Code', title: 'Matched on the product code' },
  name: { text: 'Name', title: 'Matched on the exact product name' },
  similar: { text: 'Suggested', title: 'A similar name — check it is the right product before ticking' },
  manual: { text: 'Picked', title: 'Chosen by you' }
};

const ROLE_OPTIONS = [
  { value: 'cost', label: 'Cost' },
  { value: 'sales_price', label: 'Sales price' },
  { value: 'ignore', label: 'Ignore' }
];

// A movement bigger than this is flagged: it is more often a misread digit, a
// per-tonne price against a per-bag item, or the wrong product than a real rise.
const BIG_MOVE = 0.25;

const toInput = (v) => (v === null || v === undefined ? '' : formatDecimalInput(v));
const parsePrice = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

/**
 * The roles each column starts with. A suggestion from the read is taken as
 * is; an unknown column is a Cost (Cam, 2026-09-29: supplier lists are what
 * this is mostly fed) unless another column is already the cost, and Ignore
 * after that. The reviewer can change any of it.
 */
function initialRoles(columns) {
  const suggested = columns.map((c) => c.suggested_role || 'unknown');
  // Two columns both suggested as the same role: keep the first.
  if (suggested.length === 2 && suggested[0] === suggested[1] && suggested[0] !== 'unknown') suggested[1] = 'unknown';
  const out = [];
  for (const r of suggested) {
    if (r !== 'unknown') out.push(r);
    else out.push(suggested.includes('cost') || out.includes('cost') ? 'ignore' : 'cost');
  }
  return out;
}

// kg in one of `basis`: a tonne, or one pack of `packKg`. Null if unknown.
const unitKg = (basis, packKg) => (basis === 'tonne' ? 1000 : (Number(packKg) > 0 ? Number(packKg) : null));

/**
 * What to multiply a printed price by to get the price per unit we sell by.
 * £412/T for a 25kg pack we sell per pack → × 0.025 = £10.30; £10.30 a pack for
 * one we sell per tonne → × 40 = £412. Null when a pack size is needed and not
 * known — the figures then stay as printed, and the row says so.
 */
export function unitFactor(row) {
  if (row.price_is === row.sold_per) return 1;
  const from = unitKg(row.price_is, row.pack_kg);
  const to = unitKg(row.sold_per, row.pack_kg);
  return from && to ? to / from : null;
}

/** A row's new cost / sales price, as inputs, from the column roles and its units. */
function pricesFor(row, roles) {
  const read = [row.price_1, row.price_2];
  const factor = unitFactor(row) ?? 1;
  const pick = (role) => {
    const i = roles.indexOf(role);
    if (i < 0 || read[i] === null || read[i] === undefined) return '';
    return toInput(Math.round(read[i] * factor * 10000) / 10000);
  };
  return { cost: pick('cost'), sales: pick('sales_price') };
}

/** The default "we sell it per" for an item: its unit weight, a tonne if none. */
const soldPerFor = (unitKgValue) => (Number(unitKgValue) > 0 && Number(unitKgValue) !== 1000 ? 'pack' : 'tonne');

const BASIS_OPTIONS = [{ value: 'tonne', label: 'T' }, { value: 'pack', label: 'pack' }];

function Movement({ current, next }) {
  const c = Number(current);
  const n = parsePrice(next);
  if (n === null || !Number.isFinite(n) || current === null || current === undefined || !c) return null;
  const change = (n - c) / c;
  if (Math.abs(change) < 0.0005) return <span className="pci-move same">no change</span>;
  const big = Math.abs(change) >= BIG_MOVE;
  return (
    <span
      className={`pci-move ${change > 0 ? 'up' : 'down'}${big ? ' big' : ''}`}
      title={big ? 'A large movement — check the figure, the unit and the product' : undefined}
    >
      {big && <span className="material-symbols-rounded">warning</span>}
      {change > 0 ? '+' : ''}{(change * 100).toFixed(1)}%
    </span>
  );
}

function ImportBody({
  stage, error, preview, pasteText, setPasteText, onFile, onText, onError,
  result, roles, setRole, rows, updateRow, updateRows, itemOptions, duplicates,
  selected, setSelected, scheduling, batchDate, setBatchDate, batchAuto, setBatchAuto
}) {
  useSlideOutButton([stage, rows.length, selected.size]);
  const [bulkPack, setBulkPack] = useState('');
  const ctxRows = useMemo(() => rows.map((r, i) => ({ ...r, _i: i })), [rows]);
  const ctx = useTableContextMenu({ rows: ctxRows, selectedIds: selected, getRowId: (r) => r._i });

  // The same changes offered to one row, several, or all ticked.
  const actionsFor = (ids) => [
    { key: 'price-tonne', icon: 'scale', label: 'Price is per tonne', onClick: () => updateRows(ids, { price_is: 'tonne' }) },
    { key: 'price-pack', icon: 'inventory_2', label: 'Price is per pack', onClick: () => updateRows(ids, { price_is: 'pack' }) },
    { key: 'sold-tonne', icon: 'scale', label: 'We sell per tonne', onClick: () => updateRows(ids, { sold_per: 'tonne' }) },
    { key: 'sold-pack', icon: 'inventory_2', label: 'We sell per pack', onClick: () => updateRows(ids, { sold_per: 'pack' }) },
    { key: 'use', icon: 'check_box', label: 'Queue', onClick: () => updateRows(ids, { use: true }) },
    { key: 'skip', icon: 'check_box_outline_blank', label: "Don't queue", onClick: () => updateRows(ids, { use: false }) }
  ];

  if (stage !== 'review') {
    return (
      <ImportDropZone
        busy={stage === 'reading'}
        error={error}
        preview={preview}
        pasteText={pasteText}
        setPasteText={setPasteText}
        onFile={onFile}
        onText={onText}
        onError={onError}
        title="Drop a price list here, or click to choose one"
        busyTitle="Reading the price list…"
        busyHint="A long list can take up to a minute."
        pasteLabel="Or paste the rows as text"
        pastePlaceholder={'Copy rows from an email or a spreadsheet and paste them here, e.g.\nHIPRO  Hipro Soya  £412.00\nRAPE  Rapemeal  £318.50'}
      />
    );
  }

  const conf = CONFIDENCE_COPY[result.confidence] || CONFIDENCE_COPY.medium;
  const chosen = rows.filter((r) => r.use).length;
  const unmatched = rows.filter((r) => !r.inventory_item_id).length;
  const suggested = rows.filter((r) => r.match === 'similar').length;
  const noRoles = !roles.some((r) => r !== 'ignore');

  return (
    <div className="pci-body pci-review">
      <div className="pci-main">
        {error && <div className="pci-error">{error}</div>}

        {/* Several rows at once: tick them, then set what they have in
            common here — or right-click. */}
        {selected.size > 0 && (
          <div className="import-bulk-bar">
            <span className="import-bulk-count">{selected.size} selected</span>
            <span className="import-bulk-group">Price per
              <SegToggle value={null} options={BASIS_OPTIONS} onChange={(v) => updateRows([...selected], { price_is: v })} />
            </span>
            <span className="import-bulk-group">Sell per
              <SegToggle value={null} options={BASIS_OPTIONS} onChange={(v) => updateRows([...selected], { sold_per: v })} />
            </span>
            <span className="import-bulk-group">Pack
              <input className="import-mini-input" type="number" min="0" value={bulkPack}
                onChange={(e) => setBulkPack(e.target.value)} placeholder="kg" />
              <button type="button" className="import-mini-btn" disabled={!(Number(bulkPack) > 0)}
                onClick={() => updateRows([...selected], { pack_kg: bulkPack })}>Set</button>
            </span>
            <button type="button" className="import-mini-btn" onClick={() => updateRows([...selected], { use: true })}>Queue</button>
            <button type="button" className="import-mini-btn" onClick={() => updateRows([...selected], { use: false })}>Don't queue</button>
            <button type="button" className="import-mini-btn" onClick={() => setSelected(new Set())}>Clear</button>
          </div>
        )}

        <table className="data-table pci-table" onContextMenu={ctx.onTableContextMenu}>
          <thead>
            <tr>
              <th className="pci-sel">
                <TickCheckbox
                  id="pci-select-all"
                  checked={rows.length > 0 && selected.size === rows.length}
                  ariaLabel="Select every row"
                  onChange={() => setSelected(selected.size === rows.length ? new Set() : new Set(rows.map((_, i) => i)))}
                />
              </th>
              <th className="pci-use">Use</th>
              <th>On the list</th>
              <th className="pci-product">Product</th>
              <th className="pci-units">Units</th>
              <th className="num">Cost</th>
              <th className="num">Sales price</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const dup = row.use && duplicates.has(row.inventory_item_id);
              const m = row.match ? MATCH_COPY[row.match] : null;
              const factor = unitFactor(row);
              return (
                <tr key={i} className={`${row.use ? '' : 'unused'}${dup ? ' duplicate' : ''}${selected.has(i) ? ' selected' : ''}`}
                  onContextMenu={ctx.onRowContextMenu(ctxRows[i])}>
                  <td className="pci-sel">
                    <TickCheckbox
                      id={`pci-sel-${i}`}
                      checked={selected.has(i)}
                      ariaLabel={`Select ${row.name}`}
                      onChange={() => setSelected(prev => { const n = new Set(prev); if (n.has(i)) n.delete(i); else n.add(i); return n; })}
                    />
                  </td>
                  <td className="pci-use">
                    <TickCheckbox
                      id={`pci-use-${i}`}
                      checked={row.use}
                      disabled={!row.inventory_item_id}
                      ariaLabel={`Queue ${row.name}`}
                      onChange={() => updateRow(i, { use: !row.use })}
                    />
                  </td>
                  <td className="pci-read">
                    <div className="pci-read-name">{row.name}</div>
                    <div className="pci-read-meta">
                      {row.code && <span className="pci-code">{row.code}</span>}
                      {row.unit && <span>per {row.unit}</span>}
                    </div>
                  </td>
                  <td className="pci-product">
                    <SearchableDropdown
                      compact
                      escapeOverflow
                      value={row.inventory_item_id ? String(row.inventory_item_id) : ''}
                      options={itemOptions}
                      placeholder="Choose the product…"
                      searchPlaceholder="Search by code or name…"
                      onChange={(v) => updateRow(i, { pick: v })}
                    />
                    <div className="pci-product-meta">
                      {m && <span className={`pci-match ${row.match}`} title={m.title}>{m.text}</span>}
                      {dup && <span className="pci-flag error">Also on another row</span>}
                      {row.pending && (
                        <span
                          className="pci-flag"
                          title="This product already has an unsynced change queued. Queuing this updates it; a price left blank here keeps the queued figure."
                        >
                          updates queued
                          {row.pending.cost != null ? ` cost ${formatPrice(row.pending.cost)}` : ''}
                          {row.pending.sales_price != null ? ` sale ${formatPrice(row.pending.sales_price)}` : ''}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="pci-units">
                    <div className="pci-units-row">
                      <span>£ per</span>
                      <SegToggle value={row.price_is} options={BASIS_OPTIONS} onChange={(v) => updateRow(i, { price_is: v })}
                        title="What the printed price is per" />
                      <span>pack</span>
                      <input className="import-mini-input" type="number" min="0" value={row.pack_kg ?? ''} placeholder="kg"
                        onChange={(e) => updateRow(i, { pack_kg: e.target.value })} title="One pack's weight, kg" />
                    </div>
                    <div className="pci-units-row">
                      <span>we sell per</span>
                      <SegToggle value={row.sold_per} options={BASIS_OPTIONS} onChange={(v) => updateRow(i, { sold_per: v })}
                        title="What one of this item's quantity units is — a tonne, or one pack" />
                      {factor === null
                        ? <span className="pci-flag" title="A pack size is needed to convert between tonnes and packs">set pack size</span>
                        : factor !== 1 && (
                          <span className="pci-factor" title="The printed price is multiplied by this">
                            × {factor >= 1 ? Math.round(factor * 100) / 100 : Math.round(factor * 10000) / 10000}
                          </span>
                        )}
                    </div>
                  </td>
                  {[['cost', 'current_cost'], ['sales', 'current_sales_price']].map(([field, currentKey]) => (
                    <td key={field} className="num pci-price">
                      <Input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        value={row[field]}
                        placeholder="no change"
                        onChange={(e) => updateRow(i, { [field]: e.target.value })}
                      />
                      <div className="pci-price-meta">
                        {row[currentKey] != null && <span className="muted">from {formatPrice(row[currentKey])}</span>}
                        <Movement current={row[currentKey]} next={row[field]} />
                      </div>
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>

        <p className="pci-summary">
          {chosen} of {rows.length} row{rows.length === 1 ? '' : 's'} will be queued.
          A blank price leaves that price as it is.
          {scheduling && batchAuto
            ? ` They post to Xero by themselves at 05:30 on ${formatEffectiveDate(batchDate)}.`
            : ' Nothing goes to Xero until you sync.'}
        </p>
      </div>

      <TableContextMenu
        ctx={ctx}
        rowActions={(r) => actionsFor([r._i])}
        bulkActions={(ids) => actionsFor([...ids]).map(a => ({ ...a, label: `${a.label} (${ids.size} rows)` }))}
      />

      <aside className="pci-aside">
        {scheduling && (
          <div className="pci-schedule">
            <strong>When these prices take effect</strong>
            <div className="pci-schedule-row">
              <span>Effective date</span>
              <DateInput value={batchDate} onChange={(e) => setBatchDate(e.target.value || batchDate)} />
            </div>
            <div className="pci-schedule-row">
              <label htmlFor="pci-auto-post">Auto-post on that date</label>
              <div className="checkbox-container">
                <div className="check light">
                  <input
                    id="pci-auto-post"
                    type="checkbox"
                    checked={batchAuto}
                    onChange={(e) => setBatchAuto(e.target.checked)}
                  />
                  <label htmlFor="pci-auto-post"></label>
                </div>
              </div>
            </div>
            <span className="pci-schedule-hint">
              For every row queued from this list, including changes already queued that it updates.
            </span>
          </div>
        )}

        <div className={`pci-verdict conf-${result.confidence}`}>
          <div className="pci-verdict-head">
            <span className="material-symbols-rounded">{conf.icon}</span>
            <strong>{conf.text}</strong>
          </div>
          <div className="pci-verdict-note">
            {rows.length} row{rows.length === 1 ? '' : 's'} read
            {result.source ? ` from ${result.source}` : ''}.
            {unmatched ? ` ${unmatched} not matched to a product.` : ''}
            {suggested ? ` ${suggested} suggested by similar name — tick once checked.` : ''}
            {result.notes ? ` ${result.notes}` : ''}
          </div>
        </div>

        <div className={`pci-columns${noRoles ? ' attention' : ''}`}>
          <strong>What each price column is</strong>
          <span className="pci-columns-hint">
            {noRoles
              ? 'Say which column is the cost and which the sales price — nothing is filled in until you do.'
              : 'A column the list does not label starts as the cost. Changing a column refills its figures from the list, replacing any edits to them.'}
          </span>
          {result.columns.map((col, i) => (
            <div key={i} className="pci-column">
              <span className="pci-column-heading" title={col.heading}>
                {col.heading || `Column ${i + 1}`}
              </span>
              <Dropdown options={ROLE_OPTIONS} value={roles[i]} onChange={(v) => setRole(i, v)} />
            </div>
          ))}
        </div>

        {preview && (
          <div className="pci-preview compact">
            <img src={preview} alt="The price list that was read" />
          </div>
        )}
      </aside>
    </div>
  );
}

export default function PriceChangeImportModal({ items = [], onClose, onQueued, scheduling = false, today = null }) {
  const [stage, setStage] = useState('pick'); // pick | reading | review
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [pasteText, setPasteText] = useState('');
  const [result, setResult] = useState(null);
  const [roles, setRoles] = useState([]);
  const [rows, setRows] = useState([]);
  const [queuing, setQueuing] = useState(false);
  const [selected, setSelected] = useState(() => new Set());   // row indexes ticked for bulk changes
  // One date and auto-post setting for the whole import.
  const [batchDate, setBatchDate] = useState(() => today || londonToday());
  const [batchAuto, setBatchAuto] = useState(false);

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const itemsById = useMemo(() => new Map(items.map((it) => [String(it.id), it])), [items]);

  // The picker's list, plus any item the server matched that the page's list
  // doesn't carry (it loads sold items only), so a match never shows blank.
  const itemOptions = useMemo(() => {
    const opts = items.map((it) => ({
      value: String(it.id),
      badge: it.item_code || '',
      label: it.item_name,
      searchText: `${it.item_code || ''} ${it.item_name || ''}`
    }));
    const seen = new Set(opts.map((o) => o.value));
    for (const r of rows) {
      const id = r.inventory_item_id ? String(r.inventory_item_id) : null;
      if (id && !seen.has(id)) {
        seen.add(id);
        opts.push({ value: id, badge: r.item_code || '', label: r.item_name, searchText: `${r.item_code || ''} ${r.item_name || ''}` });
      }
    }
    return opts;
  }, [items, rows]);

  const accept = useCallback((data) => {
    const read = data.rows || [];
    if (!read.length) {
      setError('No priced rows were found. Try a clearer or closer crop, or paste the rows as text.');
      setStage('pick');
      return;
    }
    const cols = (data.columns || []).length
      ? data.columns
      // A read with prices but no reported columns: offer the slots that hold figures.
      : [read.some((r) => r.price_1 != null), read.some((r) => r.price_2 != null)]
        .filter(Boolean).map(() => ({ heading: '', suggested_role: 'unknown' }));
    const startRoles = initialRoles(cols);
    setResult({ ...data, columns: cols });
    setRoles(startRoles);
    setSelected(new Set());
    setRows(read.map((r) => {
      // The unit assumptions, each editable: a price is per tonne unless the
      // list says per pack; the pack is what the list shows, else the item's
      // own; we sell per the item's unit, a tonne where it has none.
      const itemKg = Number(r.item_unit_kg) > 0 && Number(r.item_unit_kg) !== 1000 ? Number(r.item_unit_kg) : null;
      const units = {
        price_is: r.price_per === 'pack' || r.price_per === 'each' ? 'pack' : 'tonne',
        pack_kg: r.pack_kg ?? itemKg ?? '',
        sold_per: soldPerFor(r.item_unit_kg)
      };
      const prices = pricesFor({ ...r, ...units }, startRoles);
      return {
        ...r,
        ...units,
        ...prices,
        // Only a certain match with something to queue starts ticked.
        use: (r.match === 'code' || r.match === 'name') && (prices.cost !== '' || prices.sales !== '')
      };
    }));
    setStage('review');
  }, []);

  const read = useCallback(async (request) => {
    setError('');
    setStage('reading');
    try {
      const res = await fetch(`${API_URL}/api/price-changes/extract`, {
        method: 'POST', credentials: 'include', ...request
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not read that price list');
      accept(data);
    } catch (e) {
      setError(e.message);
      setStage('pick');
    }
  }, [accept]);

  const sendFile = useCallback((file) => {
    if (!file) return;
    setPreview((old) => {
      if (old) URL.revokeObjectURL(old);
      return file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
    });
    const body = new FormData();
    body.append('file', file);
    read({ body });
  }, [read]);

  const sendText = useCallback((text) => {
    if (!String(text || '').trim()) return;
    setPreview((old) => { if (old) URL.revokeObjectURL(old); return null; });
    read({ headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text }) });
  }, [read]);

  const setRole = useCallback((col, role) => {
    const next = [...roles];
    // A role belongs to one column: taking it clears the other.
    if (role !== 'ignore') next.forEach((r, j) => { if (j !== col && r === role) next[j] = 'ignore'; });
    next[col] = role;
    // Refill only the fields whose source column changed; edits to the other
    // field survive.
    const was = pricesFor({ price_1: 1, price_2: 2 }, roles);
    const now = pricesFor({ price_1: 1, price_2: 2 }, next);
    setRoles(next);
    setRows((rs) => rs.map((r) => {
      const fresh = pricesFor(r, next);
      return {
        ...r,
        cost: was.cost !== now.cost ? fresh.cost : r.cost,
        sales: was.sales !== now.sales ? fresh.sales : r.sales
      };
    }));
  }, [roles]);

  /*
   * One change to one row. A new product brings its own unit (what we sell it
   * per); a change to any unit assumption re-converts the row's cost and sale
   * price from the printed figures, replacing edits to them.
   */
  const applyPatch = useCallback((r, patch) => {
    if (patch.pick !== undefined) {
      const it = itemsById.get(patch.pick);
      const itemKg = Number(it?.unit_weight_kg) > 0 && Number(it?.unit_weight_kg) !== 1000 ? Number(it.unit_weight_kg) : null;
      const next = {
        ...r,
        inventory_item_id: patch.pick ? Number(patch.pick) : null,
        match: patch.pick ? 'manual' : null,
        item_code: it?.item_code ?? r.item_code,
        item_name: it?.item_name ?? r.item_name,
        current_cost: it ? (it.purchase_unit_price != null ? Number(it.purchase_unit_price) : null) : r.current_cost,
        current_sales_price: it ? (it.sales_unit_price != null ? Number(it.sales_unit_price) : null) : r.current_sales_price,
        item_unit_kg: it ? it.unit_weight_kg : r.item_unit_kg,
        sold_per: it ? soldPerFor(it.unit_weight_kg) : r.sold_per,
        pack_kg: r.pack_kg !== '' && r.pack_kg != null ? r.pack_kg : (itemKg ?? ''),
        // The server's queued-change lookup was for the old match; the bulk
        // queue still merges into any pending row, it just isn't shown here.
        pending: null,
        use: !!patch.pick
      };
      return { ...next, ...pricesFor(next, roles) };
    }
    if ('price_is' in patch || 'sold_per' in patch || 'pack_kg' in patch) {
      const next = { ...r, ...patch };
      return { ...next, ...pricesFor(next, roles) };
    }
    if ('use' in patch) return { ...r, use: !!patch.use && !!r.inventory_item_id };
    // Editing a price is taken as wanting the row.
    const touchesPrice = 'cost' in patch || 'sales' in patch;
    return { ...r, ...patch, ...(touchesPrice && r.inventory_item_id ? { use: true } : {}) };
  }, [itemsById, roles]);

  const updateRow = useCallback((i, patch) => {
    setRows((rs) => rs.map((r, j) => (j === i ? applyPatch(r, patch) : r)));
  }, [applyPatch]);

  const updateRows = useCallback((ids, patch) => {
    const set = new Set(ids);
    setRows((rs) => rs.map((r, j) => (set.has(j) ? applyPatch(r, patch) : r)));
  }, [applyPatch]);

  const duplicates = useMemo(() => {
    const count = new Map();
    rows.forEach((r) => { if (r.use && r.inventory_item_id) count.set(r.inventory_item_id, (count.get(r.inventory_item_id) || 0) + 1); });
    return new Set([...count].filter(([, n]) => n > 1).map(([id]) => id));
  }, [rows]);

  const chosen = rows.filter((r) => r.use);

  const queue = useCallback(async () => {
    const payload = chosen.map((r) => ({
      inventory_item_id: r.inventory_item_id,
      cost: parsePrice(r.cost),
      sales_price: parsePrice(r.sales)
    }));
    if (duplicates.size) {
      setError('The same product is ticked on more than one row. Untick or re-match one of them.');
      return;
    }
    const empty = payload.findIndex((p) => p.cost === null && p.sales_price === null);
    if (empty >= 0) {
      setError(`"${chosen[empty].name}" has no cost or sales price to queue. Enter one or untick it.`);
      return;
    }
    if (payload.some((p) => [p.cost, p.sales_price].some((v) => v !== null && (!Number.isFinite(v) || v < 0)))) {
      setError('Every price must be a number, zero or more.');
      return;
    }
    setError('');
    setQueuing(true);
    try {
      const res = await fetch(`${API_URL}/api/price-changes/bulk-queue`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(scheduling
          ? { rows: payload, effective_date: batchDate, auto_post: batchAuto }
          : { rows: payload })
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || 'Could not queue the changes');
      onQueued?.({
        created: data.created || 0,
        updated: data.updated || 0,
        ...(scheduling ? { effective_date: batchDate, auto_post: batchAuto } : {})
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setQueuing(false);
    }
  }, [chosen, duplicates, onQueued, scheduling, batchDate, batchAuto]);

  const reset = () => {
    setStage('pick'); setResult(null); setRows([]); setRoles([]); setError('');
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Import prices from a price list"
      size="xl"
      className="price-change-import"
      footer={
        <div className="buttons slideout">
          <div>
            <button data-read-safe className="btn-round btn-danger" onClick={onClose}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          {stage === 'review' && (
            <div>
              <button className="btn-slideout" onClick={reset} disabled={queuing}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">restart_alt</span></div>
                <span className="btn-label">Try another</span>
              </button>
            </div>
          )}
          <div>
            <button
              className="btn-slideout"
              onClick={queue}
              disabled={stage !== 'review' || !chosen.length || queuing}
            >
              <div className="icon-wrapper"><span className="material-symbols-rounded">playlist_add</span></div>
              <span className="btn-label">
                {queuing ? 'Queuing…'
                  : stage === 'review' ? `Queue ${chosen.length} change${chosen.length === 1 ? '' : 's'}` : 'Queue'}
              </span>
            </button>
          </div>
        </div>
      }
    >
      <ImportBody
        stage={stage}
        error={error}
        preview={preview}
        pasteText={pasteText}
        setPasteText={setPasteText}
        onFile={sendFile}
        onText={sendText}
        onError={setError}
        result={result}
        roles={roles}
        setRole={setRole}
        rows={rows}
        updateRow={updateRow}
        updateRows={updateRows}
        itemOptions={itemOptions}
        duplicates={duplicates}
        selected={selected}
        setSelected={setSelected}
        scheduling={scheduling}
        batchDate={batchDate}
        setBatchDate={setBatchDate}
        batchAuto={batchAuto}
        setBatchAuto={setBatchAuto}
      />
    </Modal>
  );
}
